import * as React from "react";
import { Helmet } from "react-helmet";
import { Prompt, useHistory, useParams, useRouteMatch } from "react-router-dom";
import store from "store2";
import * as Yup from "yup";

import {
  Analytics,
  AsyncTypeahead,
  CustomError,
  eoiSchema,
  ExternalLink,
  ShareablePasteInput,
  useEoiLocationAsyncTypeahead,
} from "@skv/core";

import type { KeyValue, Opportunity, PageEnvelope } from "@skv/core";

import {
  opportunityStore,
  useAppSettings,
  useGlobal,
  usePageEnvelope,
  useThunkDispatch,
} from "@skv/state";
import { passZippedFile } from "@skv/util";
import { useForm } from "react-yup";
import { useEoiForm, useUserDispatch } from "~/state";
import { Checkbox, Field, TextArea, ValidationError } from "~/ui";
import InPageAlert, {
  InPageAlertProps,
  InPageAlertVariant,
} from "~/ui/Alerts/InPageAlert";
import { HelpText } from "~/ui/Form/HelpText";
import OpportunityDetailsEoiRedirect from "./OpportunityDetailsEoiRedirect";

const eoiInputStore = store.session.namespace("eoiFormUserInput");

type FormValues = Yup.InferType<typeof eoiSchema>;

type EoiResult = PageEnvelope<{
  organisationMessage?: string;
  showTwoStepApplication: boolean;
  twoStepApplicationIdRedirect: string;
  twoStepPost: boolean;
  formErrors: FormErrors;
}>;

interface OpportunityDetailsEoiViewModel {
  opportunity: Opportunity;
  form: FormValues;
  genders: KeyValue[];
  formErrors: FormErrors;
}

type FormErrors = {
  [key in keyof FormValues]?: string[];
};

type OpportunityEoiFailureViewModel = {
  formErrors: FormErrors;
};

export const OpportunityDetailsEoi = (props) => {
  const [eoiForm, setEoiForm] = useEoiForm();

  const { updateUser } = useUserDispatch();
  const { seekVolunteerUrl, showApplicationNudge } = useAppSettings();

  const params = useParams<{ id: string }>();

  const dispatch = useThunkDispatch();

  const { url } = useRouteMatch();
  const { push } = useHistory();

  const { applicationName } = useAppSettings();
  const { genders } = useGlobal();

  const alertRef = React.useRef<HTMLDivElement>(null);

  const { pageEnvelope } = usePageEnvelope<OpportunityDetailsEoiViewModel>();

  const [alert, setAlert] = React.useState<InPageAlertProps>(() => {
    if (pageEnvelope.errorCode === "error:application:formvalidation") {
      return {
        variant: InPageAlertVariant.Error,
        message: (
          <>
            <p>Please fix the below errors in your form</p>
            <ul className="mb-0">
              {Object.entries(pageEnvelope.data.formErrors)
                .reduce((acc, cur) => {
                  acc = [...acc, ...cur[1]];
                  return acc;
                }, [])
                .map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
            </ul>
          </>
        ),
        show: true,
      };
    }

    return {
      variant: InPageAlertVariant.Error,
      message: "",
      show: false,
    };
  });

  const { data: opportunity } = opportunityStore.hooks.useOpportunity(
    params.id
  );
  const { name, id, locations, onlineOrRemote, organisation } = opportunity;

  const {
    isSubmitting,
    values,
    errors,
    touched,
    createSubmitHandler,
    setValue,
    setValues,
    FormProvider,
    field,
    setTouched,
    setSubmitting,
    validateForm,
  } = useForm({
    validationSchema: eoiSchema,
    defaultValues: Object.assign(
      {},
      pageEnvelope.data?.form,
      eoiForm,
      eoiInputStore.getAll()
    ),
    defaultErrors: pageEnvelope.data?.formErrors
      ? Object.entries(pageEnvelope.data?.formErrors).reduce((acc, cur) => {
          acc[cur[0]] = cur[1][0];
          return acc;
        }, {})
      : {},
    defaultTouched: pageEnvelope.data?.formErrors
      ? Object.entries(pageEnvelope.data?.formErrors).reduce((acc, cur) => {
          acc[cur[0]] = true;
          return acc;
        }, {})
      : {},
  });

  const [eoiLocations, setEoiLocations] = React.useState<string[]>([]);

  React.useEffect(() => {
    fetch("https://cdn.volunteer.com.au/data/eoi-locations.gz")
      .then(async (res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        const searchResults: string[] = await passZippedFile(res);
        if (searchResults && searchResults.length > 0) {
          setEoiLocations(searchResults);
        }
      })
      .catch(() => {
        return fetch("/api/locations/all-eoi")
          .then((res) => {
            if (!res.ok) throw new Error("Network response was not ok");
            return res.json();
          })
          .then(({ data }) => {
            if (data.locations && data.locations.length > 0) {
              setEoiLocations(data.locations);
            }
          });
      });
  }, []);

  const eoiLocationAsyncTypeahead = useEoiLocationAsyncTypeahead<string>({
    apiUrl: (query) => `/api/locations/eoi?q=${query}`,
    onSuccess: (data) => data,
    defaultOptions: eoiLocations,
  });

  const isFormPartiallyFilled = Object.values(values).some((n) => !!n);

  React.useEffect(() => {
    const showNudgeWarning = (e: BeforeUnloadEvent) => {
      if (isFormPartiallyFilled) {
        e.preventDefault();
        e.returnValue = "Are you sure? You have unsaved changes.";
        return e.returnValue;
      }
    };

    if (showApplicationNudge) {
      window.addEventListener("beforeunload", showNudgeWarning);

      return () => {
        window.removeEventListener("beforeunload", showNudgeWarning);
      };
    }
  }, [isFormPartiallyFilled, showApplicationNudge]);

  const handleSubmit = React.useMemo(() => {
    return createSubmitHandler(async (values) => {
      setAlert((prev) => ({
        ...prev,
        visible: false,
      }));

      /* TODO : Do this in onChange handler? */
      setEoiForm((prev) => ({
        ...prev,
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        gender: values.gender,
        location: values.location,
        phone: values.phone,
        qualification: values.qualification,
        yearOfBirth: values.yearOfBirth,
      }));

      await fetch(`/api/opportunity/${params.id}/apply`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...values,
          shareableLink: values.shareableLink ? values.shareableLink : null,
        }),
      })
        .then<EoiResult>((res) => {
          if (res.ok) {
            return res.json();
          }

          throw Error("Unhandled network error");
        })
        .then(({ data: viewModel, errorCode, errorMessage, success }) => {
          // EOI submitted successfully
          if (success) {
            updateUser((prev) => ({
              ...prev,
              applicationCount: prev.applicationCount + 1,
            }));
            setValues(() => ({} as FormValues));
            if (
              "showTwoStepApplication" in viewModel &&
              viewModel.showTwoStepApplication
            )
              push(
                `${url}/redirect/${viewModel.twoStepApplicationIdRedirect}`,
                viewModel
              );
            else push(`${url}/success`, viewModel);
            return;
          }

          // Handle EOI submission errors
          switch (errorCode) {
            case "error:application:formvalidation": {
              setAlert((prev) => ({
                ...prev,
                variant: InPageAlertVariant.Error,
                message: (
                  <>
                    <p>Please fix the below errors in your form</p>
                    <ul className="mb-0">
                      {Object.entries(
                        (viewModel as OpportunityEoiFailureViewModel).formErrors
                      )
                        .reduce((acc, cur) => {
                          acc = [...acc, ...cur[1]];
                          return acc;
                        }, [])
                        .map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                    </ul>
                  </>
                ),
                show: true,
              }));
              alertRef.current?.focus();
              break;
            }
            case "error:application:alreadyexists":
            case "error:application:limitreached": {
              dispatch({
                type: "OPPORTUNITY:ERROR:SET",
                payload: {
                  error: new CustomError(errorMessage, errorCode),
                },
              });
              break;
            }
            default:
              throw Error("Unhandled pageEnvelope errorCode");
          }
        })
        .catch((err) => {
          // network error
          setAlert((prev) => ({
            ...prev,
            variant: InPageAlertVariant.Error,
            children: (
              <>
                We could not submit your expression of interest at this time.
                Please try again or{" "}
                <a href={`${seekVolunteerUrl}/help-and-support/contact-us`}>
                  contact support
                </a>
                .
              </>
            ),
            show: true,
          }));

          alertRef.current?.focus();
        })
        .finally(() => setSubmitting(false));
    });
  }, [
    createSubmitHandler,
    setEoiForm,
    params.id,
    updateUser,
    setValues,
    push,
    url,
    dispatch,
    seekVolunteerUrl,
    setSubmitting,
  ]);

  /* Wrap field.onChange to track form input and persist the session */
  const fieldProxy = React.useMemo(() => {
    const handleChange = (e) => {
      const { name, value } = e.target;

      /* TODO : TEMP FOR GENDER, figure out better way */
      if (name === "gender") {
        eoiInputStore.set(name, parseInt(value, 10));
      } else {
        eoiInputStore.set(name, value);
      }
      field.onChange(e);
    };

    return {
      onBlur: field.onBlur,
      onChange: handleChange,
    };
  }, [field]);

  const locationFieldProps = React.useMemo(() => {
    const handleChange = (locs) => {
      setValues((v) => {
        v.location = locs[0];
        return v;
      }, true);
      eoiInputStore.set("location", locs[0] || "");
    };

    const handleBlur = (e) => {
      setTouched((t) => {
        t.location = true;
        return t;
      });
      validateForm({ touch: false });
    };

    return {
      onChange: handleChange,
      onBlur: handleBlur,
    };
  }, [setTouched, setValues, validateForm]);

  const positionLocation = React.useMemo(() => {
    let positions = "This position is ";
    if (locations.length > 0) {
      positions += `in ${locations.map((n) => n.name).join(", ")}`;
    }
    if (locations.length === 0 || onlineOrRemote) {
      positions += !!locations.length
        ? " / Remote Friendly"
        : "Online or Remote";
    }
    return positions;
  }, [locations, onlineOrRemote]);

  // Third-party application redirect
  if (opportunity.applicationRedirectUrl?.length > 0) {
    return (
      <OpportunityDetailsEoiRedirect
        partnerName={opportunity.partnerName}
        applicationRedirectUrl={opportunity.applicationRedirectUrl}
      />
    );
  }

  // Local application form
  return (
    <>
      <FormProvider>
        <Helmet>
          <title>{`Apply for ${name}, ${organisation.name}`}</title>
          <meta
            name="description"
            content={`Express your interest in Volunteering with ${name} - ${organisation.category} with ${organisation.name}. Enquire now at ${applicationName}`}
          />
          <meta
            property="og:title"
            content={`Apply for ${name}, ${organisation.name}`}
          />
          <meta
            property="og:description"
            content={`Express your interest in Volunteering with ${name} - ${organisation.category} with ${organisation.name}. Enquire now at ${applicationName}`}
          />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        {showApplicationNudge && (
          <Prompt
            when={isFormPartiallyFilled && !isSubmitting}
            message="Are you sure you want to leave this page?"
          />
        )}
        <div className="mb-4">
          <p>
            Enter your details below to apply for this opportunity.
            {organisation.twoStepApplication && (
              <>
                <br />
                Afterwards, you will be instructed to complete your application
                on the {organisation.name} website.
              </>
            )}
          </p>
          <p>
            To apply for volunteer positions, you must reside in{" "}
            <strong>Australia</strong>.
            <br />
            {positionLocation}.
          </p>

          {alert.show && <InPageAlert {...alert} />}

          <span className="d-inline-block mb-4">
            All fields marked with * are required
          </span>
          <form
            method="POST"
            className="nsw-form mt-0"
            onSubmit={(e) => {
              handleSubmit(e);
            }}
          >
            <div className="row">
              <div className="col-md-6">
                <Field
                  id="firstName"
                  name="firstName"
                  label="First Name"
                  type="text"
                  autoComplete="given-name"
                  aria-required
                  {...fieldProxy}
                />
              </div>
              <div className="col-md-6">
                <Field
                  id="lastName"
                  name="lastName"
                  label="Last Name"
                  type="text"
                  autoComplete="family-name"
                  aria-required
                  {...fieldProxy}
                />
              </div>
            </div>

            <Field
              id="phone"
              name="phone"
              label="Phone Number"
              helpText="Help organisations contact you"
              type="text"
              autoComplete="tel"
              aria-required
              {...fieldProxy}
            />

            <Field
              id="email"
              name="email"
              label="Email address"
              type="email"
              autoComplete="email"
              aria-required
              {...fieldProxy}
            />

            {/* TODO : Autosuggest */}
            <div className="nsw-form__group">
              <label className="nsw-form__label" htmlFor="autosuggest-location">
                Location *
              </label>
              <AsyncTypeahead
                id="autosuggest-location"
                placeholder="Enter suburb, town or city"
                {...eoiLocationAsyncTypeahead}
                touched={!!touched.location}
                error={errors.location ?? undefined}
                inputProps={{
                  name: "location",
                  className: "nsw-form__input",
                }}
                wrapperProps={{
                  className: "nsw-form__label",
                }}
                aria-required
                aria-describedby={
                  touched.location && !!errors.location
                    ? `${id}-error-message`
                    : ""
                }
                {...locationFieldProps}
                defaultInputValue={
                  eoiInputStore.get("location") ||
                  pageEnvelope.data?.form?.location ||
                  ""
                }
                hideValidation={true}
              />
              {errors.location && touched.location && (
                <ValidationError
                  errorId="location-error-message"
                  message={errors.location}
                />
              )}
            </div>

            <ShareablePasteInput
              application="NSW"
              touched={touched.shareableLink}
              error={errors.shareableLink}
              value={values.shareableLink}
              field={field}
              onPaste={(text) => {
                if (text) {
                  setTouched((prev) => ({
                    ...prev,
                    shareableLink: true,
                  }));

                  let normalized = text;
                  if (text.includes("seek") && !text.startsWith("http")) {
                    normalized = `https://${text}`;
                  }

                  setValue("shareableLink", normalized, true);
                }
              }}
              customValidationError={
                <ValidationError
                  errorId="shareableLink-error-message"
                  message={errors.shareableLink}
                />
              }
            />

            <TextArea
              {...fieldProxy}
              id="qualification"
              name="qualification"
              label="Interests, skills and qualifications"
              optional
              helpText="Maximum 500 characters."
              rows={3}
            />

            <div className="nsw-form__group">
              <fieldset
                className="nsw-form__fieldset"
                aria-describedby="gender-help-text"
              >
                <legend className="nsw-form__legend mb-0">
                  Gender <small>(optional)</small>
                </legend>
                <HelpText
                  id="gender-help-text"
                  className="nsw-form__helper"
                  helpText="Some opportunities are gender specific"
                />
                <div
                  className="d-flex flex-column flex-lg-row align-items-start gap-4"
                  css={`
                    label {
                      margin-top: 0;
                    }
                  `}
                >
                  {genders.map(({ id, name }) => (
                    <Checkbox
                      key={id}
                      id={`gender-${name}`}
                      name="gender"
                      type="radio"
                      label={name}
                      value={id}
                      checked={values.gender === id}
                      {...fieldProxy}
                    />
                  ))}
                </div>
              </fieldset>
            </div>

            <Field
              {...fieldProxy}
              id="yearOfBirth"
              name="yearOfBirth"
              label="Year of Birth"
              placeholder="e.g 1991"
              autoComplete="bday-year"
              optional
              helpText="Some organisations have age limited insurance."
            />

            <p className="mt-3">
              By applying, you agree to our{" "}
              <ExternalLink
                href={seekVolunteerUrl + "/legal/privacy-statement"}
              >
                Privacy Policy
              </ExternalLink>
              . You agree to receive messages about volunteer opportunities and
              other updates about products and features from SEEK Volunteer. You
              can unsubscribe at any time.
            </p>

            <button
              className="btn btn-primary"
              css={`
                padding-left: 2.5rem;
                padding-right: 2.5rem;
              `}
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Sending..."
                : organisation.twoStepApplication
                ? "Submit and continue"
                : "Apply"}
            </button>
          </form>
        </div>
        <Analytics
          data={{
            subSection: "opportunity",
            subSubSection: "opportunity:application+form",
            subSubSubSection: "opportunity:application+form",
            pageTitle: "opportunity:application+form",
            pageType: "opportunity:form",
            organisationId: organisation.id,
            opportunityId: id,
          }}
        />
      </FormProvider>
    </>
  );
};
